import { useLingui } from '@lingui/react/macro';

import { deckDepth } from '@/domain/templates/preview-stacks';
import { getShiftColour, SHIFT_PILL_CLASSES } from '@/domain/templates/shift-colours';
import { cn } from '@/lib/utils';

interface ShiftStackCardProps {
  /** Shift type name — drives the label and colour. */
  typeName: string;
  /** Formatted time range, e.g. `09:00–17:00`. */
  time: string;
  /** Total heads in the stack — shown as the `×N` badge and the deck depth. */
  count: number;
  /** Ghost styling for the in-progress (draft) pattern. */
  draft?: boolean;
  /**
   * Number of unfilled slots in the deck (proposal view only). When > 0 the
   * card gets a dashed warning outline and an "N short" line.
   */
  shortCount?: number;
}

/**
 * A grouped shift "deck" in the stacked preview: a single coloured card for all
 * shifts of one type + time on a day, with a `×N` count badge and a
 * deck-of-cards look — extra cards peek out straight above the front card, each
 * separated by the card in front of it casting an upward shadow onto its
 * exposed top strip. The deck gets deeper with the count (decorative, hidden
 * from assistive tech — only the count is announced).
 */
export function ShiftStackCard({
  typeName,
  time,
  count,
  draft = false,
  shortCount = 0,
}: ShiftStackCardProps) {
  const { t } = useLingui();
  const classes = SHIFT_PILL_CLASSES[getShiftColour(typeName)];
  const surface = draft
    ? cn(classes.draft, 'border-dashed')
    : shortCount > 0
      ? cn(classes.solid, 'border-dashed border-destructive/70')
      : classes.solid;
  const depth = deckDepth(count);
  // How far each card behind peeks out above the one in front of it. The
  // upward shadow separates each exposed strip from the card in front.
  const layerOffset = 4;
  const deckShadow = 'shadow-[0_-2px_3px_-1px_rgba(0,0,0,0.18)]';

  return (
    <div
      data-testid="shift-stack"
      data-count={count}
      data-draft={draft ? 'true' : 'false'}
      className="relative"
      // Reserve room above the card for the deck cards, which peek out above it
      // — otherwise they overlap the row above.
      style={{ marginTop: depth * layerOffset }}
    >
      {/* Deepest first so the nearer cards (and the front card) paint on top and
          cast their upward shadow onto the strip peeking out above. */}
      {Array.from({ length: depth }, (_, layer) => {
        const fromBack = depth - layer;
        return (
          <div
            key={layer}
            aria-hidden
            className={cn('absolute inset-0 rounded-[3px] border', surface, deckShadow)}
            style={{ transform: `translateY(${-fromBack * layerOffset}px)` }}
          />
        );
      })}
      <div
        className={cn(
          'relative min-h-[48px] rounded-[3px] border px-2 pt-0.5 pb-0 text-left leading-tight',
          surface,
          deckShadow,
        )}
      >
        <span className="absolute -top-1.5 -right-1.5 inline-flex min-w-6 items-center justify-center rounded-full bg-foreground px-1.5 text-sm font-semibold text-background">
          ×{count}
        </span>
        <span className="block truncate text-sm font-semibold">{typeName}</span>
        <span className="block text-sm font-semibold opacity-90">{time}</span>
        {shortCount > 0 && (
          <span className="block text-sm font-semibold text-destructive">
            {t`${shortCount} short`}
          </span>
        )}
      </div>
    </div>
  );
}
